import PDFMerger from "pdf-merger-js/browser"
import React, { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import moment from "moment"
import {
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  Icon,
  MenuItem,
  Pagination,
  Select,
} from "@mui/material"
import { pdf } from "@react-pdf/renderer"
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDTypography from "components/MDTypography"
import DashboardLayout from "examples/LayoutContainers/DashboardLayout"
import DashboardNavbar from "examples/Navbars/DashboardNavbar"
import translate from "utils/translate"
import MDDatePicker from "components/MDDatePicker"
import MDInput from "components/MDInput"
import MDSnackbar from "components/MDSnackbar"
import PackageDataTable from "layouts/exchanges/DataTable/PackageDataTable"
import { PackageControllerProvider } from "context/packages"
import { blobToBase64, substractDays } from "utils/functions/global"

// Service
import * as packageService from "services/packages/package.service"
import { printLazajellaPDF } from "services/packages/package.service"

// Data
import packagesDataTable from "layouts/exchanges/data/packagesDataTable"

import { PackageDocument } from "./pdf/PackageDocument"
import { printChahinPackages } from "layouts/packages/pdf/ChahinPackageDocument"
import { PackageDocumentMylrez } from "./pdf/PackageDocumentMylrez"
import { translateAccountName } from "utils/translate/status"
import { useRecoilState, useRecoilValue, useResetRecoilState } from "recoil"
import { exchangePackagesState, exchangeRowIdsState } from "./recoil/states"

function Exchanges() {
  const navigate = useNavigate()
  const selectedRowIdsFromStore = useRecoilValue(exchangeRowIdsState)
  const [recoilPackages, setRecoilPackages] = useRecoilState(exchangePackagesState)
  const resetSelectedRowIds = useResetRecoilState(exchangeRowIdsState)
  const [account, setAccount] = useState("-")
  const [isPrinted, setIsPrinted] = useState("-")
  const [companyName, setCompanyName] = useState(0)
  const [statusFilter, setStatusFilter] = useState("Echange Reçu")
  const [startDatePicker, setStartDatePicker] = useState(substractDays())
  const [endDatePicker, setEndDatePicker] = useState(moment().format("YYYY-MM-DD"))
  const [search, setSearch] = useState("")
  const [packageList, setPackageList] = useState([])
  const [backupPackageList, setBackupPackageList] = useState([])
  const [isDataLoading, setIsDataLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [packageTotal, setPackageTotal] = useState(0)
  const [snackbarObject, setSnackbarObject] = useState({
    open: false,
    content: "",
    color: "primary",
    icon: "",
  })
  const [isLoadingPDF, setIsLoadingPDF] = useState(false)
  const [urlToPrint, setUrlToPrint] = useState("")
  let lazajellaBarcodes = []
  let alamanaBarcodes = []
  let mylerzBarcodes = []
  let chahinPackages = []
  const rowsPerPage = 10

  // Handle start date picker change event
  const handleStartDatePicker = (date) => {
    const formatDate = moment(date[0]).format("YYYY-MM-DD") || ""
    setStartDatePicker(formatDate)
  }

  // Handle end date picker change event
  const handleEndDatePicker = (date) => {
    const formatDate = moment(date[0]).format("YYYY-MM-DD") || ""
    setEndDatePicker(formatDate)
  }

  // intialize start date and end date
  const intializeDates = () => {
    const startFrom = substractDays()
    // const startFrom = moment().subtract(30, "d").format("YYYY-MM-DD")
    const today = moment().format("YYYY-MM-DD")
    setStartDatePicker(startFrom)
    setEndDatePicker(today)
  }

  // Handle search by client names and phone numbers
  const handleSearchChange = async (evt) => {
    try {
      const word = evt.target.value
      setSearch(word)
      if (word.length >= 3) {
        const companyNameEmptyString = companyName === 0 ? "" : companyName
        const statusFilterEmptyString = statusFilter === "-" ? "" : statusFilter

        // unconfirmed package
        const isConfirmed = true

        const searchPackages = await packageService.findAllPackages(
          companyNameEmptyString,
          statusFilterEmptyString,
          startDatePicker,
          endDatePicker,
          currentPage,
          word,
          isConfirmed
        )

        setPackageList(searchPackages.data.packageList)
      } else {
        setPackageList(backupPackageList)
      }
    } catch (error) {
      setPackageList(backupPackageList)
    }
  }

  // Handle open snackbar
  const handleOpenSnackbar = (status, content, icon) => {
    setSnackbarObject({
      ...snackbarObject,
      open: true,
      content,
      color: status,
      icon,
    })
  }

  // Handle close snackbar
  const handleCloseSnackbar = () => {
    setSnackbarObject({
      ...snackbarObject,
      open: false,
    })
  }

  // Handle company event
  const handleChangeCompany = (evt) => {
    const targetValue = evt.target.value
    setCompanyName(targetValue)
  }

  // Handle status event
  const handleChangeStatus = (evt) => {
    const targetValue = evt.target.value
    setStatusFilter(targetValue)
  }

  // Handle printed event
  const handleChangePrinted = (evt) => {
    const targetValue = evt.target.value
    setIsPrinted(targetValue)
  }

  // Handle account event
  const handleChangeAccount = (evt) => {
    const targetValue = evt.target.value
    setAccount(targetValue)
  }

  // Reset filters
  const resetFilters = () => {
    setCompanyName(0)
    setStartDatePicker("")
    setEndDatePicker("")
    setSearch("")
    setStatusFilter("Echange Reçu")
    setIsPrinted("-")
    setAccount("-")
    intializeDates()
    onResetRowIds()
  }

  // Reset recoil store
  const onResetRowIds = () => {
    resetSelectedRowIds()
    navigate(0)
  }

  // Spread rowIds object keys
  const getRowIdsKeys = () => {
    return Object.keys(selectedRowIdsFromStore)
  }

  // Gel all packages
  const getAllPackages = async () => {
    try {
      setIsDataLoading(true)
      const companyNameEmptyString = companyName === 0 ? "" : companyName
      const statusFilterEmptyString = statusFilter === "-" ? "" : statusFilter
      const isPrintedEmptyString = isPrinted === "-" ? "" : isPrinted
      const accountEmptyString = account === "-" ? "" : account

      // unconfirmed package
      const isConfirmed = true

      const { data } = await packageService.findAllPackages(
        companyNameEmptyString,
        statusFilterEmptyString,
        startDatePicker,
        endDatePicker,
        currentPage,
        search,
        isPrintedEmptyString,
        accountEmptyString,
        isConfirmed
      )
      setRecoilPackages((prevPackageList) => [...prevPackageList, data.packageList])
      setPackageList(data.packageList)
      setBackupPackageList(data.packageList)
      setPackageTotal(data.packageTotal)
      setIsDataLoading(false)
    } catch (error) {
      setPackageList([])
    }
  }

  // Handle pagination change
  const handleChangePagination = (event, value) => {
    setCurrentPage(value)
  }

  // Calculate number of pages
  const pageCount = (total) => {
    if (total <= rowsPerPage) return rowsPerPage
    let result = total / rowsPerPage
    if (result % 1 !== 0) result = Math.floor(result) + 1
    return result
  }

  // Handle print alamana PDF package
  const printAlamanaPackage = async () => {
    const alamanaMerger = new PDFMerger()
    const flatRecoilPackages = recoilPackages.flat()
    for (let index = 0; index < alamanaBarcodes.length; index++) {
      const barcode = alamanaBarcodes[index]
      const packageItem = flatRecoilPackages.find((packageElement) => packageElement.barcode === barcode)
      const { status, data } = await packageService.generateCodes(barcode)
      if (status === 200) {
        const pdfDocument = pdf(
          <PackageDocument dataDocument={packageItem} generatedQRCode={data.qrcode} generatedBarcode={data.barcode} />
        ).toBlob()
        await pdfDocument.then(async (blob) => {
          const convertBlobToBase64 = await blobToBase64(blob)
          await alamanaMerger.add(convertBlobToBase64)
        })
      }
    }

    const mergedPdf = await alamanaMerger.saveAsBlob()
    const url = URL.createObjectURL(mergedPdf)
    setUrlToPrint(url)
  }

  // Handle print mylerz PDF package
  const printMylerzPackage = async () => {
    const mylerzMerger = new PDFMerger()
    const flatRecoilPackages = recoilPackages.flat()
    for (let index = 0; index < mylerzBarcodes.length; index++) {
      const barcode = mylerzBarcodes[index]
      const packageItem = flatRecoilPackages.find((packageElement) => packageElement.barcode === barcode)
      const { status, data } = await packageService.generateCodes(barcode)
      if (status === 200) {
        const pdfDocument = pdf(
          <PackageDocumentMylrez
            dataDocument={packageItem}
            generatedQRCode={data.qrcode}
            generatedBarcode={data.barcode}
          />
        ).toBlob()
        await pdfDocument.then(async (blob) => {
          const convertBlobToBase64 = await blobToBase64(blob)
          await mylerzMerger.add(convertBlobToBase64)
        })
      }
    }

    const mergedPdf = await mylerzMerger.saveAsBlob()
    const url = URL.createObjectURL(mergedPdf)
    setUrlToPrint(url)
  }

  const printLazajellaPackage = async () => {
    const lazajellaMerger = new PDFMerger()
    for (let index = 0; index < lazajellaBarcodes.length; index++) {
      const barcode = lazajellaBarcodes[index]
      const result = await printLazajellaPDF(barcode)
      const base64EncodedPDF = result.data
      await lazajellaMerger.add(`data:application/pdf;base64, ${base64EncodedPDF}`)
    }
    const mergedPdf = await lazajellaMerger.saveAsBlob()
    const url = URL.createObjectURL(mergedPdf)
    setUrlToPrint(url)
  }

  // Handle print chahin PDF packages (labels generated in the browser)
  const printChahinPackage = async () => {
    try {
      const { url, warnings } = await printChahinPackages(chahinPackages)
      if (warnings.length > 0) handleOpenSnackbar("warning", warnings[0], "warning")
      setUrlToPrint(url)
    } catch (error) {
      handleOpenSnackbar("error", error.message || translate.something_wrong, "error")
    }
  }

  // Handle print selected rows
  const printSelectedPackages = async () => {
    lazajellaBarcodes = []
    alamanaBarcodes = []
    mylerzBarcodes = []
    chahinPackages = []
    setIsLoadingPDF(true)
    try {
      const flatRecoilPackages = recoilPackages.flat()
      for (let index = 0; index < getRowIdsKeys().length; index++) {
        const barcode = getRowIdsKeys()[index]
        const packageItem = flatRecoilPackages.find((packageElement) => packageElement.barcode === barcode)
        if (packageItem.companyName === "lazajella") {
          lazajellaBarcodes.push(barcode)
        } else if (packageItem.companyName === "alamana") {
          alamanaBarcodes.push(barcode)
        } else if (packageItem.companyName === "chahin") {
          chahinPackages.push(packageItem)
        } else if (packageItem.companyName === "mylerz") {
          mylerzBarcodes.push(barcode)
        }
      }
      if (companyName === "lazajella") {
        printLazajellaPackage()
      } else if (companyName === "alamana") {
        printAlamanaPackage()
      } else if (companyName === "chahin") {
        printChahinPackage()
      } else if (companyName === "mylerz") {
        console.log("mylerzBarcodes ===>", mylerzBarcodes)
        printMylerzPackage()
      } else {
        handleOpenSnackbar("error", "Aucune entreprise sélectionnée", "error")
      }
      setIsLoadingPDF(false)
    } catch (error) {
      setIsLoadingPDF(false)
      handleOpenSnackbar("error", translate.something_wrong, "error")
    }
  }

  // Refresh all status
  const refreshAllStatus = async () => {
    setIsLoadingPDF(true)
    try {
      const packageInstance = await packageService.refreshStatus()
      if (packageInstance.status === 200) {
        handleOpenSnackbar("success", "Toutes les statues ont été rafraîchies", "check_circle")
        setIsLoadingPDF(false)
        navigate(0)
      }
    } catch (error) {
      setIsLoadingPDF(false)
      handleOpenSnackbar("error", translate.something_wrong, "error")
    }
  }

  // Refresh all status
  const refreshMissingPackages = async () => {
    setIsLoadingPDF(true)
    try {
      const packageInstance = await packageService.refreshMissingPackages()
      if (packageInstance.status === 200) {
        handleOpenSnackbar("success", "Tous les colis manquants ont été rafraichis", "check_circle")
        setIsLoadingPDF(false)
        navigate(0)
      }
    } catch (error) {
      setIsLoadingPDF(false)
      handleOpenSnackbar("error", translate.something_wrong, "error")
    }
  }

  // Handle row id
  const getRowId = (row, relativeIndex, parent) => {
    const barcode = row.barcode.props.children
    return parent ? [parent.id, barcode].join(".") : barcode
  }

  useEffect(() => {
    getAllPackages()
    return () => {
      if (window.location.pathname !== "/echange") {
        onResetRowIds()
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDatePicker, endDatePicker, companyName, currentPage, search, statusFilter, isPrinted, account])

  const handleOpenPdf = (url) => {
    window.open(url, "_blank")
    setUrlToPrint("")
    onResetRowIds()
  }

  return (
    <PackageControllerProvider>
      <DashboardLayout>
        <DashboardNavbar absolute />
        <MDBox pt={6} pb={3}>
          <Grid container spacing={6}>
            <Grid item xs={12}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <MDTypography mt={2} gutterBottom variant="body1">
                    Filtrage des colis
                  </MDTypography>
                  <MDBox>
                    <Grid container display="flex" justifyContent="space-between" alignItems="center" spacing={2}>
                      <Grid item xs={12} lg={2}>
                        <MDInput fullWidth label="Cherche ici..." value={search} onChange={handleSearchChange} />
                      </Grid>
                      <Grid item xs={12} lg={2}>
                        <FormControl fullWidth margin="normal" variant="standard" sx={{ minWidth: 120 }} size="small">
                          <Select
                            labelId="id-filter-company_id-label"
                            id="id-filter-company_id"
                            value={companyName}
                            onChange={handleChangeCompany}
                            label="Company"
                            size="medium"
                            sx={{ p: 1 }}
                          >
                            <MenuItem value={0}>
                              <em>Toutes les entreprises</em>
                            </MenuItem>
                            <MenuItem value="alamana">Alamana</MenuItem>
                            <MenuItem value="lazajella">Lazajella</MenuItem>
                            <MenuItem value="mylerz">Mylerz</MenuItem>
                            <MenuItem value="chahin">Chahin</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} lg={2}>
                        <FormControl fullWidth margin="normal" variant="standard" sx={{ minWidth: 120 }} size="small">
                          <Select
                            labelId="id-filter-status-label"
                            id="id-filter-status"
                            value={statusFilter}
                            onChange={handleChangeStatus}
                            label="Status"
                            size="medium"
                            sx={{ p: 1 }}
                          >
                            <MenuItem value="-" disabled>
                              <em>Tous les statuts</em>
                            </MenuItem>
                            <MenuItem value="Echange Reçu">Echange Reçu</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} lg={2}>
                        <FormControl fullWidth margin="normal" variant="standard" sx={{ minWidth: 120 }} size="small">
                          <Select
                            labelId="id-filter-printed-label"
                            id="id-filter-printed"
                            value={isPrinted}
                            onChange={handleChangePrinted}
                            label="Printed"
                            size="medium"
                            sx={{ p: 1 }}
                          >
                            <MenuItem value="-">
                              <em>Tout sélectionner</em>
                            </MenuItem>
                            <MenuItem value={true}>Imprimé</MenuItem>
                            <MenuItem value={false}>Non imprimé</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} lg={2}>
                        <FormControl fullWidth margin="normal" variant="standard" sx={{ minWidth: 120 }} size="small">
                          <Select
                            labelId="id-filter-accounts-label"
                            id="id-filter-accounts"
                            value={account}
                            onChange={handleChangeAccount}
                            label="Account"
                            size="medium"
                            sx={{ p: 1 }}
                          >
                            <MenuItem value="-">
                              <em>Tout sélectionner</em>
                            </MenuItem>
                            <MenuItem value="account1">{translateAccountName("account1")}</MenuItem>
                            {companyName !== "alamana" && (
                              <MenuItem value="account2">{translateAccountName("account2")}</MenuItem>
                            )}
                            <MenuItem value="account3">{translateAccountName("account3")}</MenuItem>
                            <MenuItem value="account4">{translateAccountName("account4")}</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} lg={1}>
                        <MDDatePicker
                          onChange={handleStartDatePicker}
                          value={startDatePicker}
                          input={{
                            placeholder: "Sélectionner la date de début",
                            fullWidth: true,
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} lg={1}>
                        <MDDatePicker
                          onChange={handleEndDatePicker}
                          value={endDatePicker}
                          input={{
                            placeholder: "Sélectionner la date de fin",
                            fullWidth: true,
                          }}
                        />
                      </Grid>
                    </Grid>
                  </MDBox>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  variant="gradient"
                  bgColor="info"
                  borderRadius="lg"
                  coloredShadow="info"
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <MDTypography variant="h6" color="white">
                    Gestion des colis
                  </MDTypography>
                  <MDTypography variant="h6" color="white">
                    Nombre des colis sélectionné ({Object.keys(selectedRowIdsFromStore).length})
                  </MDTypography>
                </MDBox>
                <MDBox pt={3}>
                  {isDataLoading ? (
                    <MDBox
                      p={26}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <MDTypography component="p" variant="body2">
                        Loading...
                      </MDTypography>
                    </MDBox>
                  ) : (
                    <PackageDataTable
                      table={packagesDataTable(packageList)}
                      isSorted={false}
                      noEndBorder={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      getRowId={getRowId}
                      onResetRowIds={() => onResetRowIds()}
                    />
                  )}
                  <Grid container p={3} pb={1}>
                    <Grid item>
                      <MDButton
                        fullWidth
                        variant="text"
                        color="dark"
                        size="medium"
                        onClick={printSelectedPackages}
                        disabled={isLoadingPDF}
                      >
                        <Icon>{isLoadingPDF ? <CircularProgress size={16} color="inherit" /> : "print"}</Icon>&nbsp;
                        imprimer tous les colis
                      </MDButton>
                    </Grid>
                    <Grid item>
                      <MDButton
                        fullWidth
                        variant="text"
                        color="dark"
                        size="medium"
                        onClick={refreshAllStatus}
                        disabled={isLoadingPDF}
                      >
                        <Icon>{isLoadingPDF ? <CircularProgress size={16} color="inherit" /> : "refresh"}</Icon>
                        &nbsp;actualiser tous les statuts
                      </MDButton>
                    </Grid>
                    <Grid item>
                      <MDButton
                        fullWidth
                        variant="text"
                        color="dark"
                        size="medium"
                        onClick={refreshMissingPackages}
                        disabled={isLoadingPDF}
                      >
                        <Icon>{isLoadingPDF ? <CircularProgress size={16} color="inherit" /> : "hourglass_top"}</Icon>
                        &nbsp;actualiser tous les colis manquants
                      </MDButton>
                    </Grid>
                    <Grid item>
                      <MDButton
                        fullWidth
                        variant="text"
                        color="dark"
                        size="medium"
                        onClick={resetFilters}
                        disabled={isLoadingPDF}
                      >
                        <Icon>{isLoadingPDF ? <CircularProgress size={16} color="inherit" /> : "restart_alt"}</Icon>
                        &nbsp;réinitialiser tous les filtres
                      </MDButton>
                    </Grid>
                    {urlToPrint !== "" && (
                      <Grid item>
                        <MDButton
                          type="submit"
                          variant="gradient"
                          color="warning"
                          onClick={() => handleOpenPdf(urlToPrint)}
                        >
                          Ouvrir les colis pour imprimer
                        </MDButton>
                      </Grid>
                    )}
                  </Grid>
                  {packageTotal > 10 && (
                    <Grid p={3} pt={0} container alignItems="center" justifyContent="space-between">
                      <Grid item sm={12} md={6} pt={3}>
                        <MDTypography component="p" variant="body2">
                          Affichage {currentPage} à {rowsPerPage} sur {packageTotal} colis
                        </MDTypography>
                      </Grid>
                      <Grid item pt={3}>
                        <Pagination
                          count={pageCount(packageTotal)}
                          defaultPage={currentPage}
                          page={currentPage}
                          onChange={handleChangePagination}
                          color="info"
                          variant="outlined"
                        />
                      </Grid>
                    </Grid>
                  )}
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      </DashboardLayout>
      <MDSnackbar
        color={snackbarObject.color}
        icon={snackbarObject.icon}
        title="Colis échangé"
        content={snackbarObject.content}
        open={snackbarObject.open}
        close={handleCloseSnackbar}
        dateTime=""
        sx={{
          float: "left",
        }}
      />
    </PackageControllerProvider>
  )
}

export default Exchanges
